/**
 * Language core: the dictionaries, interpolation and the context object.
 *
 * PROVENANCE: copied from C:\Horizon\web\src\i18n\core.ts @ 7d1c316. CHANGES: the storage key is olivia's; the
 * dictionary type is Olivia's. `t(key, vars)` is the only way a component prints interface text; keys are typed,
 * so a misspelt key is a compile error and a missing Swedish string cannot exist (sv.ts is typed against en.ts).
 */

import { createContext } from 'react'

import { en, type MessageKey } from './en'
import { sv } from './sv'

export type Language = 'en' | 'sv'
export type { MessageKey }

export const LANGUAGES: { value: Language; label: string; locale: string }[] = [
  // Each language names itself: a reader who cannot read the current one must still find their own.
  { value: 'en', label: 'English', locale: 'en-GB' },
  { value: 'sv', label: 'Svenska', locale: 'sv-SE' },
]

const DICTIONARIES = { en, sv } as const
const STORAGE_KEY = 'olivia.language'

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string

/** Fill `{name}` placeholders. A placeholder with no value stays visible rather than vanishing. */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match))
}

/**
 * A count of one takes the key's `.one` form when the dictionaries have one ("1 försenad", not "1 försenade";
 * "1 open action", not "1 open actions"). The count is the `n` placeholder; a string with two counts is written
 * as labels with numbers ("årshjulspunkter: 0 · åtgärder: 1") so that no form is needed.
 */
export function translator(language: Language): Translate {
  const dict: Record<string, string> = DICTIONARIES[language]
  const english: Record<string, string> = en
  return (key, vars) => {
    const one = vars?.n === 1 || vars?.n === '1' ? `${key}.one` : null
    const k = one && one in english ? one : key
    return interpolate(dict[k] ?? english[k] ?? key, vars)
  }
}

export function localeOf(language: Language): string {
  return LANGUAGES.find((l) => l.value === language)?.locale ?? 'en-GB'
}

/** Whether a string is a message key (server errors arrive as `errors.<CODE>`). */
export function isMessageKey(key: string): key is MessageKey {
  return key in en
}

export function readStoredLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'en' || stored === 'sv') return stored
    return navigator.language.toLowerCase().startsWith('sv') ? 'sv' : 'en'
  } catch {
    return 'en'
  }
}

export function storeLanguage(language: Language): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, language)
  } catch {
    // Storage disabled by policy: the choice holds for this tab.
  }
}

export interface LanguageContextValue {
  language: Language
  setLanguage: (language: Language) => void
  t: Translate
}

export const LanguageContext = createContext<LanguageContextValue | null>(null)
